/**
 * useRibbon
 *
 * Resolves the icon for a GameRibbon.
 *
 * The core API now stores the full CDN URL in `ribbon.icon` (uploaded via the
 * centralized icon library). When present, the URL is returned as-is and
 * rendered through an <img>. Ribbons without a configured icon fall back to
 * the local NuxtIcon mapping below (assets/icons/ribbons/*.svg).
 */

const RIBBON_ICON_MAP: Record<string, string> = {
  CASHBACK: 'ribbons/cashback',
  TORNEIO: 'ribbons/torneio',
  LANÇAMENTO: 'ribbons/lancamento',
  LANCAMENTO: 'ribbons/lancamento',
  POPULAR: 'ribbons/popular',
  'DOUBLE EXP': 'ribbons/double_exp',
  DOUBLE_EXP: 'ribbons/double_exp',
};

const FALLBACK_ICON = 'ribbons/nao_identificado';

const CLOUDFLARE_HOST = 'imagedelivery.net';
const CLOUDFLARE_VARIANT_RE = /\/(public|mobile|ipad)$/;

/**
 * Cloudflare Images requires a variant suffix (e.g. `/public`) — without it
 * the URL 404s. Mirrors core's `getIconDisplayUrl`.
 */
function resolveIconUrl(url: string): string {
  if (!url.includes(CLOUDFLARE_HOST)) return url;
  if (CLOUDFLARE_VARIANT_RE.test(url)) return url;

  return `${url}/public`;
}

export interface RibbonIcon {
  url?: string;
  name?: string;
}

export function useRibbon() {
  function getRibbonIcon(ribbon: GameRibbon): RibbonIcon {
    if (ribbon.icon) return { url: resolveIconUrl(ribbon.icon) };

    const normalized = ribbon.name?.trim().toUpperCase() ?? '';

    return { name: RIBBON_ICON_MAP[normalized] ?? FALLBACK_ICON };
  }

  /**
   * Inline style object for the ribbon pill background.
   * Uses the hex color from the API response.
   */
  function getRibbonStyle(ribbon: GameRibbon): Record<string, string> {
    return {
      background: ribbon.color || '',
      color: ribbon.text_color || '#ffffff',
    };
  }

  /**
   * Inline style for a tag pill using its own hex color.
   * Text is always white — dark enough for all tag colors in the seeder.
   */
  function getTagStyle(tag: GameTag): Record<string, string> {
    return {
      background: tag.color || '',
      color: '#ffffff',
    };
  }

  return { getRibbonIcon, getRibbonStyle, getTagStyle };
}
